import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import type { Page } from '../workbench-shared';
export interface Navigation {entries:Page[];index:number}
const overlays=new Set<string>();
const browser=()=>Boolean(window.minglunCloudLimits);

/** A dismissible surface occupies one browser-history entry without changing the URL. */
export function useBrowserDismiss(open:boolean,close:()=>void){
  const token=useRef(crypto.randomUUID()),live=useRef(close),mounted=useRef(false);live.current=close;
  useEffect(()=>{
    if(!open||!browser())return;
    const id=token.current;mounted.current=true;overlays.add(id);
    if(history.state?.minglunOverlay!==id){
      const stale=history.state?.minglunOverlay && !overlays.has(history.state.minglunOverlay);
      history[stale?'replaceState':'pushState']({...history.state,minglunOverlay:id},'');
    }
    const pop=()=>{if(history.state?.minglunOverlay!==id)live.current();};
    addEventListener('popstate',pop);
    return()=>{
      mounted.current=false;removeEventListener('popstate',pop);overlays.delete(id);
      queueMicrotask(()=>{if(!mounted.current&&history.state?.minglunOverlay===id)history.back();});
    };
  },[open]);
}

export function useBrowserNavigation(account:string,navigation:Navigation,setNavigation:Dispatch<SetStateAction<Navigation>>,allowed:Page[]){
  const previous=useRef(navigation),allowedRef=useRef(allowed);allowedRef.current=allowed;
  useEffect(()=>{
    if(!browser())return;
    history.replaceState({...history.state,minglunNavigation:{account,...navigation}},'');
    const pop=()=>{
      const next=history.state?.minglunNavigation;
      if(!next || next.account!==account || !Array.isArray(next.entries) || !Number.isInteger(next.index) || next.index<0 || next.index>=next.entries.length || !next.entries.every((page:Page)=>allowedRef.current.includes(page)))return;
      const value={entries:next.entries as Page[],index:next.index as number};
      previous.current=value;setNavigation(value);
    };
    addEventListener('popstate',pop);return()=>removeEventListener('popstate',pop);
  },[account]);
  useEffect(()=>{
    if(!browser()||previous.current===navigation)return;
    history.pushState({...history.state,minglunOverlay:undefined,minglunNavigation:{account,...navigation}},'');previous.current=navigation;
  },[navigation,account]);
  return {back:()=>browser()?history.back():setNavigation(n=>({...n,index:Math.max(0,n.index-1)})),forward:()=>browser()?history.forward():setNavigation(n=>({...n,index:Math.min(n.entries.length-1,n.index+1)}))};
}
